import React, { createContext, useContext, useState, useEffect } from 'react';
import { ALL_TRADES, ROLES } from '../utils/constants';

const TenantContext = createContext();

const DEFAULT_ORGANIZATIONS = [
  {
    id: 'org_acme_heating',
    name: 'Acme Heating & Gas Ltd',
    slug: 'acme-heating',
    plan: 'Enterprise',
    trades: ['Gas Safety', 'Plumbing', 'Oil'],
    accreditations: ['Gas Safe', 'OFTEC'],
    gasSafeRegNumber: '654321',
    oftecRegNumber: 'C1093',
    branding: {
      primaryColor: '#2563eb',
      logoUrl: null,
      companyAddress: '14 Industrial Way, London EC1A 1BB',
      contactPhone: '020 7946 0912',
      contactEmail: 'certificates@acmeheating.co.uk'
    },
    members: [
      { id: 'usr_acme_owner', name: 'Sarah Mitchell', email: 'sarah@acmeheating.co.uk', role: ROLES.OWNER, active: true },
      { id: 'usr_acme_eng1', name: 'Tom Bradley', email: 'tom@acmeheating.co.uk', role: ROLES.ENGINEER, active: true }
    ]
  },
  {
    id: 'org_apex_renewables',
    name: 'Apex Renewables & Electrical',
    slug: 'apex-renewables',
    plan: 'Pro Team',
    trades: ['Electrical', 'HVAC / Renewables'],
    accreditations: ['NICEIC', 'MCS', 'NAPIT'],
    gasSafeRegNumber: '',
    niceicRegNumber: '604209-000',
    branding: {
      primaryColor: '#059669',
      logoUrl: null,
      companyAddress: '8 Eco Park Avenue, Manchester M1 2HY',
      contactPhone: '0161 496 0382',
      contactEmail: 'ops@apexrenewables.co.uk'
    },
    members: [
      { id: 'usr_apex_owner', name: 'James Okafor', email: 'james@apexrenewables.co.uk', role: ROLES.OWNER, active: true },
      { id: 'usr_apex_admin', name: 'Priya Shah', email: 'priya@apexrenewables.co.uk', role: ROLES.ADMIN, active: true },
      { id: 'usr_apex_eng1', name: 'Dan Whitfield', email: 'dan@apexrenewables.co.uk', role: ROLES.ENGINEER, active: true }
    ]
  },
  {
    id: 'org_solo_plumbing',
    name: 'Solo Gas & Plumbing Services',
    slug: 'solo-plumbing',
    plan: 'Solo Contractor',
    trades: ['Gas Safety', 'Plumbing'],
    accreditations: ['Gas Safe'],
    gasSafeRegNumber: '554433',
    branding: {
      primaryColor: '#d97706',
      logoUrl: null,
      companyAddress: 'Flat 3, 22 High Street, Bristol BS1 2AW',
      contactPhone: '0117 496 0123',
      contactEmail: 'john@sologas.co.uk'
    },
    members: [
      { id: 'usr_solo_owner', name: 'John Carter', email: 'john@sologas.co.uk', role: ROLES.OWNER, active: true }
    ]
  }
];

// Fallback trades for legacy locally-stored orgs created before trade gating existed
const LEGACY_TRADE_DEFAULTS = DEFAULT_ORGANIZATIONS.reduce((acc, org) => {
  acc[org.id] = org.trades;
  return acc;
}, {});

const normalizeOrganization = (org) => {
  if (!org || typeof org !== 'object') return null;
  const defaults = LEGACY_TRADE_DEFAULTS[org.id];
  return {
    ...org,
    trades: Array.isArray(org.trades)
      ? org.trades
      : defaults || [...ALL_TRADES],
    accreditations: Array.isArray(org.accreditations) ? org.accreditations : [],
    members: Array.isArray(org.members) ? org.members : [],
    branding: org.branding || {}
  };
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState(() => {
    try {
      const saved = localStorage.getItem('saas_organizations');
      const parsed = saved ? JSON.parse(saved) : DEFAULT_ORGANIZATIONS;
      // Backfill trades/members for records persisted by older app versions
      return (Array.isArray(parsed) ? parsed : DEFAULT_ORGANIZATIONS)
        .map(normalizeOrganization)
        .filter(Boolean);
    } catch (e) {
      console.warn('Failed to parse stored organizations, using defaults:', e);
      return DEFAULT_ORGANIZATIONS;
    }
  });

  const [activeTenantId, setActiveTenantId] = useState(() => {
    return localStorage.getItem('saas_active_tenant_id') || DEFAULT_ORGANIZATIONS[0].id;
  });

  const activeTenant = organizations.find(org => org.id === activeTenantId) || organizations[0];

  useEffect(() => {
    localStorage.setItem('saas_organizations', JSON.stringify(organizations));
  }, [organizations]);

  useEffect(() => {
    localStorage.setItem('saas_active_tenant_id', activeTenantId);
  }, [activeTenantId]);

  const switchTenant = (tenantId) => {
    const target = organizations.find(org => org.id === tenantId);
    if (target) {
      setActiveTenantId(tenantId);
    }
  };

  const updateTenantBranding = (brandingUpdate) => {
    setOrganizations(prev => prev.map(org => {
      if (org.id === activeTenantId) {
        return {
          ...org,
          branding: { ...org.branding, ...brandingUpdate }
        };
      }
      return org;
    }));
  };

  /**
   * Merge-update the active tenant with arbitrary top-level fields
   * (name, trades, accreditations, license numbers, members, ...).
   * Returns the updated organization.
   */
  const updateTenant = (updates) => {
    let updated = null;
    setOrganizations(prev => prev.map(org => {
      if (org.id === activeTenantId) {
        updated = { ...org, ...updates };
        return updated;
      }
      return org;
    }));
    return updated;
  };

  /**
   * Insert or merge an organization by id (used by AuthContext when syncing
   * the authenticated user's PocketBase organization into local state).
   */
  const upsertOrganization = (org) => {
    if (!org?.id) return;
    setOrganizations(prev => {
      const existing = prev.find(o => o.id === org.id);
      if (existing) {
        return prev.map(o => (o.id === org.id ? { ...o, ...org } : o));
      }
      return [...prev, normalizeOrganization(org)];
    });
  };

  return (
    <TenantContext.Provider value={{
      organizations,
      activeTenant,
      activeTenantId,
      switchTenant,
      updateTenantBranding,
      updateTenant,
      upsertOrganization
    }}>
      {children}
    </TenantContext.Provider>
  );
};
